<template>
  <div class="about-page">
    <AboutHero :title="adminHeading" />
    <AboutMarquee :items="marqueeItems" />
    <AboutStory />
    <AboutTimeline
      ref="timeline"
      :items="timeline"
      :active-items="timelineActive"
    />
    <AboutCustomers :posts="igPosts" />
    <AboutPillars />
    <AboutBrandsStrip :brands="brands" />
    <AboutSplitCta />
  </div>
</template>

<script>
import "./about_us.css";

import AboutHero from "~/components/about/AboutHero.vue";
import AboutMarquee from "~/components/about/AboutMarquee.vue";
import AboutStory from "~/components/about/AboutStory.vue";
import AboutTimeline from "~/components/about/AboutTimeline.vue";
import AboutPillars from "~/components/about/AboutPillars.vue";
import AboutBrandsStrip from "~/components/about/AboutBrandsStrip.vue";
import AboutCustomers from "~/components/about/AboutCustomers.vue";
import AboutSplitCta from "~/components/about/AboutSplitCta.vue";
import { cdnUrl } from "~/utils/cdn";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

const MARQUEE_ITEMS = [
  "100% Authentic",
  "In-House Inventory",
  "Free U.S. Standard Shipping",
  "10,000+ Happy Customers",
  "Official & Licensed Suppliers Only",
  "Ships From Our Own Warehouse",
  "Affordable Luxury Eyewear",
  "EST. 2020",
  "No Drop Shipping · Ever",
];

const BRANDS = [
  { name: "Saint Laurent", href: "/saint-laurent" },
  { name: "Tom Ford", href: "/tom-ford" },
  { name: "Prada", href: "/prada" },
  { name: "Dior", href: "/dior" },
  { name: "Gucci", href: "/gucci" },
  { name: "Miu Miu", href: "/miu-miu-eyewear" },
  { name: "Loewe", href: "/loewe" },
  { name: "Celine", href: "/celine" },
  { name: "Versace", href: "/versace" },
  { name: "Off-White", href: "/off-white-eyewear" },
  { name: "Burberry", href: "/burberry" },
  { name: "Fendi", href: "/fendi-eyewear" },
];

export default {
  name: "AboutUs",
  setup() {
    return useStaticPageSeo({
      title: "About Hooligans Eyewear | Authentic Designer Eyewear",
      heading: "Affordable Luxury Eyewear",
      description:
        "Meet Hooligans Eyewear, an independent retailer offering 100% authentic designer sunglasses and eyeglasses from in-house inventory.",
    });
  },
  components: {
    AboutHero,
    AboutMarquee,
    AboutStory,
    AboutTimeline,
    AboutCustomers,
    AboutPillars,
    AboutBrandsStrip,
    AboutSplitCta,
  },
  data() {
    return {
      marqueeItems: MARQUEE_ITEMS,
      brands: BRANDS,
      timelineActive: [false, false, false, false, false],
      timeline: [
        {
          tag: "The Beginning",
          heading: "Hooligans is Born",
          body: "David and Neli founded Hooligans Eyewear with a shared passion for fashion and a mission to make premium designer frames accessible to everyone.",
          year: "2020",
          yellow: true,
        },
        {
          tag: "First Milestone",
          heading: "1,000 Sales in Year One",
          body: "In our very first year, we surpassed 1,000 sales — a milestone that confirmed we were on the right path and that our customers valued authenticity above all.",
          year: "2020",
          yellow: false,
        },
        {
          tag: "Growth",
          heading: "Expanding Across Marketplaces",
          body: "We grew our presence across eBay, Poshmark, Mercari, Grailed, and other marketplaces — connecting with thousands of customers across the United States.",
          year: "2021–23",
          yellow: false,
        },
        {
          tag: "Our Own Home",
          heading: "HooligansEyewear.com Launches",
          body: "We launched our own website to give customers a better, more flexible experience — with our full catalog, every item in stock in our warehouse, and zero drop shipping.",
          year: "2024",
          yellow: false,
        },
        {
          tag: "Today",
          heading: "10,000+ Happy Customers",
          body: "From Gucci to Miu Miu, Prada to Tom Ford — we now carry hundreds of authentic styles, loved by over 10,000 customers and growing every day.",
          year: "2026+",
          yellow: true,
        },
      ],
      igPosts: [
        {
          username: "katrinpashynska",
          location: "Miami, FL",
          initials: "KP",
          avatarClass: "",
          img: cdnUrl("/image/HOOLIGANS+WEB/About+Us/ig-1-katrinpashynska.png"),
          imgStyle: { objectPosition: "center 25%" },
          permalink: "https://instagram.com/katrinpashynska",
          likes: 1284,
          caption:
            "obsessed with my new frames from @hooliganseyewear \u{1F5A4} arrived in 2 days, 100% authentic.",
          tags: ["#CelineEyewear", "#HooligansEyewear"],
          time: "2 days ago",
          preLiked: false,
        },
        {
          username: "lily.tkachuk_",
          location: "New York, NY",
          initials: "LT",
          avatarClass: "ig-avatar-blue",
          img: cdnUrl("/image/HOOLIGANS+WEB/About+Us/ig-2-lily.png"),
          imgStyle: { objectPosition: "center 30%" },
          permalink: "https://instagram.com/lily.tkachuk_",
          likes: 986,
          caption:
            "just got these from @hooliganseyewear. best deal I found online \u2014 no question.",
          tags: ["#PradaSunglasses", "#DesignerEyewear"],
          time: "5 days ago",
          preLiked: false,
        },
        {
          username: "alana_yav",
          location: "Los Angeles, CA",
          initials: "AY",
          avatarClass: "ig-avatar-green",
          img: cdnUrl("/image/HOOLIGANS+WEB/About+Us/ig-3-alana.png"),
          imgStyle: { objectPosition: "center 25%" },
          permalink: "https://instagram.com/alana_yav",
          likes: 2107,
          caption:
            "ordered Monday, wore them Wednesday \u2708\uFE0F @hooliganseyewear never disappoints.",
          tags: ["#TomFord", "#OOTD"],
          time: "1 week ago",
          preLiked: false,
        },
      ],
    };
  },
  mounted() {
    this._reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._setupRevealObserver();
    this._setupCounters();
    this._setupTimelineScroll();
  },
  beforeUnmount() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
    if (this._countObserver) {
      this._countObserver.disconnect();
      this._countObserver = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
      window.removeEventListener("resize", this._scrollHandler);
      this._scrollHandler = null;
    }
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },

    _setupCounters() {
      const counters = this.$el.querySelectorAll("[data-count-target]");
      if (!counters.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        return;
      }

      this._countObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._animateCounter(entry.target);
              this._countObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );
      counters.forEach((el) => this._countObserver.observe(el));
    },

    _animateCounter(el) {
      const target = parseInt(el.dataset.countTarget, 10);
      if (!isFinite(target)) return;
      const suffix = el.dataset.suffix || "";
      const isYear = !suffix;
      const duration = isYear ? 1400 : 1600;
      const start = performance.now();
      const isK = suffix === "K+";

      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const value = Math.round(target * eased);

        let display;
        if (isYear) {
          display = String(value);
        } else if (isK) {
          display = Math.round(value / 1000) + "K+";
        } else if (suffix === "%") {
          display = value + "%";
        } else {
          display = value + suffix;
        }
        el.textContent = display;

        if (t < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    },

    _setupTimelineScroll() {
      this._scrollHandler = () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = null;
          this._updateTimeline();
        });
      };
      window.addEventListener("scroll", this._scrollHandler, { passive: true });
      window.addEventListener("resize", this._scrollHandler, { passive: true });
      this._updateTimeline();
    },

    _updateTimeline() {
      const timelineComp = this.$refs.timeline;
      if (!timelineComp) return;
      const track = timelineComp.getTrackEl();
      const progress = timelineComp.getProgressEl();
      if (!track || !progress) return;

      const rect = track.getBoundingClientRect();
      const trackTop = rect.top + window.scrollY;
      const trackH = rect.height;
      const scrolled = window.scrollY + window.innerHeight * 0.65;
      const filled = Math.max(0, Math.min(1, (scrolled - trackTop) / trackH));
      progress.style.height = filled * trackH + "px";

      const items = track.querySelectorAll(".timeline-item");
      const newActive = [...this.timelineActive];
      let changed = false;
      items.forEach((item, i) => {
        const itemRect = item.getBoundingClientRect();
        const dotY = itemRect.top + itemRect.height * 0.15;
        if (dotY < window.innerHeight * 0.72 && !newActive[i]) {
          newActive[i] = true;
          changed = true;
        }
      });
      if (changed) {
        this.timelineActive = newActive;
      }
    },
  },
};
</script>
